import { BidToken } from '../common/BidToken';
import { SuitSymbol } from '../common/SuitSymbol';

/**
 * The Preempts dashboard card's icon (feature 033, FR-030; client request 2026-10-16), after the
 * client's reference picture: a 3S card leaping upward — a preempt IS a jump bid — over a fanned
 * row of one-level cards, on green felt inside a brass frame, with the four suits in a row
 * beneath. Grown from the inventory row glyph that preceded it (2026-10-15), which drew only the
 * card and the rise.
 *
 * Built from the site's own pieces rather than an image: every bid is a `BidToken` and every suit
 * the shared `SuitSymbol` the constitution's Suit Symbol Display standard requires (sized to the
 * icon by `.preempt-door-icon .suit-glyph` in index.css), and the colours are the site's felt,
 * brass, cream and ink, so the icon reads as part of the dashboard rather than a pasted picture
 * and stays crisp at every pixel density. Decorative, hidden from assistive tech — the card's
 * title already says "Preempts".
 */

/** The one-level cards the 3S leaps over, fanned left to right behind it. */
const ONE_LEVEL_CARDS = [
  { bid: '1C', className: 'left-[3px] -rotate-12' },
  { bid: '1D', className: 'left-[16px] -rotate-6' },
  { bid: '1H', className: 'left-[33px] rotate-6' },
  { bid: '1S', className: 'left-[46px] rotate-12' },
] as const;

const SMALL_CARD =
  'absolute top-[24px] inline-flex h-[20px] w-[15px] items-center justify-center rounded-[2px] border border-brass bg-cream text-[8px] font-bold leading-none text-ink';

export function PreemptDoorIcon() {
  return (
    <span
      className="preempt-door-icon relative inline-flex h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-felt ring-1 ring-brass"
      aria-hidden="true"
    >
      {/* The rise: a brass arrow straight up from the card's top edge. */}
      <span className="absolute left-1/2 top-[2px] -translate-x-1/2 font-sans text-[14px] font-bold leading-none text-brass">
        ↑
      </span>

      {ONE_LEVEL_CARDS.map(({ bid, className }) => (
        <span key={bid} className={`${SMALL_CARD} ${className}`}>
          <BidToken bid={bid} />
        </span>
      ))}

      {/* The 3S card in flight: cream face, brass edge, tilted a touch, lifted off the fan by its
          shadow. Drawn after the fan so it sits in front. */}
      <span className="absolute left-1/2 top-[12px] inline-flex h-[30px] w-[22px] -translate-x-1/2 -rotate-6 items-center justify-center rounded-[3px] border border-brass bg-cream font-serif text-[13px] font-bold leading-none text-ink shadow-[0_1px_3px_rgba(0,0,0,0.5)]">
        <BidToken bid="3S" />
      </span>

      {/* The suit row on the felt. `suit-on-dark` is what keeps the black suits visible there. */}
      <span className="suit-on-dark absolute inset-x-0 bottom-[3px] flex justify-center gap-[3px] text-[9px] leading-none">
        <SuitSymbol suit="S" />
        <SuitSymbol suit="H" />
        <SuitSymbol suit="D" />
        <SuitSymbol suit="C" />
      </span>
    </span>
  );
}
